import userStore from '../stores/UserStore';
import manageStore from '../stores/ManageStore';
import schoolStore from '../containers/main/schools/store';
import {NotificationSoundStatus, Roles, StudentStatus, SubscriptionPlans, UserStatus} from '../constants/enums';
import {BIRTHDAY_DISCOUNT, DEFAULT_SUBSCRIPTION_PLAN, demoStudentName, GRACE_PERIOD, STUDENT_STATUS_INFO, SUBSCRIPTION_PLANS} from '../constants/constants';
import isNumber from 'lodash/isNumber';
import isUndefined from 'lodash/isUndefined';
import {getStorage} from './storageHelper';
import guideStore from '../containers/common/guide/store';
import onBoardingStore from '../containers/main/onBoarding/store';

/**
 * Check if the user cognito md5 id fits the required id
 * @param id
 * @returns {boolean}
 */
export function isOwner(id) {
    return userStore.user.id === id;
}

/**
 * get the user token
 * @returns {*}
 */
export function getToken() {
    if (userStore.user) {
        return userStore.user.token;
    }
    return null;
}

export function getGoogleAccessToken() {
    return userStore.user.googleAccessToken;
}

export function isUserFirstTime() {
    return userStore.user.firstTime
}

/**
 * get the user email
 * @returns {*}
 */
export function getEmail() {
    return userStore.user.email;
}

/**
 * get the user name
 * @returns {*}
 */
export function getName() {
    return userStore.user.name;
}

export function getFirstName() {
    try {
        return userStore.user.givenName || userStore.user.name;
    } catch (err) {
        return '';
    }
}

export function getLastName() {
    try {
        return userStore.user.givenName && userStore.user.familyName ? userStore.user.familyName : '';
    } catch (err) {
        return '';
    }
}

export function getVideoMeetingUrl() {
    return (userStore.user.userProperties || {}).videoMeetingUrl;
}

export function getVideoMeetingActive() {
    return (userStore.user.userProperties || {}).videoMeetingActive;
}

export function getNotificationSoundConfig() {
    return (userStore.user?.userProperties || {}).notifConf || NotificationSoundStatus.Enabled;
}


export function getStudentFirstName(fullName) {
    try {
        return fullName.split(' ')[0] || '';
    } catch (err) {
        return '';
    }
}

export function getStudentLastName(fullName) {
    try {
        return fullName.split(' ').slice(1).join(' ');
    } catch (err) {
        return '';
    }
}

/**
 * get the user avatar
 * @returns {*}
 */
export function getUserAvatar() {
    return userStore.user.avatar;
}

export function setUserAvatar(url) {
    userStore.updateUser('custom:avatar', url, 'avatar');
}

export function getDefaultAvatar() {
    return `/avatar/default/${Math.floor(Math.random() * 8) + 10}.jpg`;
}

export function isDefaultAvatar(srcStr) {
    return !srcStr || srcStr?.indexOf('avatar/default') !== -1;
}

export async function setUserName(givenName) {
    const fullName = givenName + (userStore.user.familyName ? (` ${userStore.user.familyName}`) : '');
    await userStore.updateUser('name', fullName, 'name');
    return userStore.updateUser('given_name', givenName, 'givenName');
}

export async function setUserFamilyName(familyName) {
    const givenName = userStore.user.givenName ? userStore.user.givenName : userStore.user.name;
    const fullName = `${givenName} ${familyName}`;
    await userStore.updateUser('name', fullName, 'name');
    return userStore.updateUser('family_name', familyName, 'familyName');
}

export function getGoogleCalendarId() {
    return userStore.user.googleCalendarId;
}

export function setGoogleCalendarId(calendarId) {
    return userStore.updateUser('custom:google_calendar_id', calendarId, 'googleCalendarId');
}

export function setUserInstruments(instrumentsStr) {
    userStore.updateUser('custom:instruments', JSON.stringify([instrumentsStr]), 'instruments');
}

export function setUserAddress(address) {
    userStore.updateUser('address', address, 'address');
}

export function setPreviewShowed(value) {
    userStore.updateUser('custom:showPreview', value.toString(), 'showPreview');
}

export async function setUserInfo(userInfo) {
    await userStore.updateUserInfo(userInfo);
}


/**
 * get the user id
 * @returns {*}
 */
export function getId() {
    return userStore.user.id;
}

/**
 * get the entire user
 */
export function getUser() {
    return userStore.user;
}

/**
 * get the entire user
 */
export function getRegisteredAt() {
    return new Date(userStore.user.registeredAt);
}

export function getNextChargeDate() {
    return userStore.user.paymentNextChargeDate;
}

/**
 * get the user owner details
 */
export function getOwnerDetails() {
    return {
        owner: {
            id: userStore.user.id,
            name: userStore.user.name,
            avatar: userStore.user.avatar
        }
    };
}


export function getUserReady() {
    try {
        return userStore.initializedLocale && userStore.user.id && userStore.isAuthenticated;
    } catch (err) {
        return false;
    }
}

/**
 * get user new format sender data
 */
export function getNewSenderData(activeChat) {
    const senderInfo = {
        fromId: userStore.user.id,
        timestamp: Date.now()
    };
    senderInfo.from = {
        id: userStore.user.id,
        name: userStore.user.name,
        avatar: userStore.user.avatar
    };

    if (isTeacher() || isSchoolAdmin()) {
        senderInfo.teacher = senderInfo.from;
        senderInfo.student = manageStore.getStudent(activeChat);
    } else {
        senderInfo.student = senderInfo.from;
        senderInfo.teacher = manageStore.getTeacher(activeChat);
    }

    return senderInfo;
}

// TODO: get role from server
export function getRole() {
    if (isNumber(userStore.user.role)) {
        return userStore.user.role;
    }
    return null;
}

export function isTeacher() {
    return getRole() === Roles.Teacher;
}

export function isStudent() {
    return getRole() === Roles.Student;
}

export function isAdmin() {
    return getRole() === Roles.Admin;
}

export function isSchoolAdmin() {
    return getRole() === Roles.SchoolAdmin;
}

export function isMusicoAdmin() {
    return getRole() === Roles.Admin && getId() === '44d326a2-2173-459d-9d9c-f3ba37456ef1';
}

export function isGuest() {
    return !userStore.user || !userStore.user.id;
}


export function getRoleName(role) {
    switch (role) {
        case Roles.Teacher:
            return 'Teacher';
        case Roles.Student:
            return 'Student';
        case Roles.Admin:
            return 'Admin';
        default:
            return 'unknown';
    }
}

export function getCurrentUserRoleName() {
    return getRoleName(userStore.user.role);
}

export function getUserStatus() {
    return userStore.user.userStatus;
}

export function getPaymentStatus() {
    return userStore.user.paymentStatus;
}

export function getUserPaymentType() {
    return userStore.user.paymentType;
}

export function getUserPlan() {
    return userStore.user.paymentPlanType || SubscriptionPlans.Trial;
}


export function isActiveUserPlan() {
    return getUserPlan() === SubscriptionPlans.Unlimited;
}

export function getUserPlanDetails() {
    return SUBSCRIPTION_PLANS.find(p => p.key === getUserPlan()) || DEFAULT_SUBSCRIPTION_PLAN;
}

export function getStudentSeats() {
    return userStore.user.paymentQuantity;
}

export function getTotalAmount() {
    let couponDiscount = getCouponDiscount();
    if (getUserPlan() === SubscriptionPlans.Unlimited) {
        couponDiscount = 1;
    }
    return Math.round(couponDiscount * userStore.user.paymentTotalAmount * 10) / 10 || 0;// todo
}

export function getCouponDiscount() {
    return userStore.user.paymentCouponDiscount || BIRTHDAY_DISCOUNT;
}

export function paymentMethodId() {
    return userStore.user.paymentMethodId || '';// todo
}

export function getUserCurrency() {
    return userStore.user.paymentCurrency || 'USD';// todo
}

export function getActiveStudents() {
    return manageStore.liveStudentsLength;
}

export function getUserTrialDaysLeft() {
    return userStore.trialDaysLeft;
}

export function getUserGraceDaysLeft() {
    return userStore.trialDaysLeft > 0 ? 0 : userStore.trialDaysLeft + GRACE_PERIOD;
}

export function isSchoolUser() {
    return userStore.user && !isUndefined(userStore.user.schoolId);
}

export function getSchoolId() {
    return userStore.user.schoolId;
}

export function getSchoolAvatar() {
    return userStore.user.schoolAvatar;
}

export function getSchoolName() {
    return userStore.user.schoolName;
}

export function getSchool() {
    return schoolStore.activeSchool || {};
}

export function getUserProperties() {
    return userStore.user.userProperties || {};
}

export function getUserMainInstrument() {
    return userStore.user.instruments && userStore.user.instruments.length > 0 ? userStore.user.instruments[0] : userStore.user.instruments;
}

export function getUserInstruments() {
    return userStore.user.instruments;
}

export function setUserProperties(userProps) {
    return userStore.setUserProperties(userProps);
}


export function getDeviceLatency() {
    const latencyStr = getStorage().getItem('musico-latency-frames');
    try {
        const latency = parseFloat(latencyStr);
        return isNaN(latency) ? latencyStr : latency;
    } catch (err) {
        return latencyStr;
    }
}

export function setDeviceLatency(value) {
    return getStorage().setItem('musico-latency-frames', value);
}

export function getUserABProb() {
    let result = getUserProperties().a_b_Prob;
    if (isUndefined(result)) {
        result = Math.round(Math.random() * 100) / 100;
        setUserProperties({a_b_Prob: result});
    }
    return result;
}

// todo move these to a separate file

export function isDuringTimelineFirstGuide() {
    return guideStore.isDuringTimelineFirstGuide;
}

export function isDuringTimelineFirstGuideAndRunning() {
    return guideStore.isDuringTimelineFirstGuideAndRunning;
}

export function showReferralProgramBanner() {
    if (isTeacher() && !isSchoolUser()) {
        if (isActiveUserPlan()) {
            return getUserProperties().showReferral;
        }
    }
    return false;
}

export function showReferralPaymentBanner() {
    if (isTeacher() && !isSchoolUser()) {
        if (!isActiveUserPlan() && getUserReferrer()) {
            if (getUserProperties().showReferral === undefined) {
                return true;
            }
            return getUserProperties().showReferral;
        }
    }
    return false;
}


export function getUserReferrer() {
    return userStore.user.source === 'referrer';
}

export function getRefCode() {
    return userStore.user.refCode;
}

export const shouldDemoCallAppear = () => !(
    (!isTeacher() && !isGuest())
        || isSchoolUser()
        || onBoardingStore.showOnboarding
        || getUserStatus() === UserStatus.Active
        || showReferralPaymentBanner()
);

export const isDemoStudent = studentName => studentName === demoStudentName;


export const getStatusKey = (status) => {
    switch (status) {
        case StudentStatus.Activated:
            return 'active';
        case StudentStatus.Inactive:
            return 'inactive';
        case StudentStatus.Trial:
            return 'trial';
        case StudentStatus.Invited:
            return 'pending';
        case StudentStatus.Demo:
            return 'demo';
        case StudentStatus.NotInvited:
            return 'notConnected';
        case StudentStatus.TrialEnded:
            return 'inactive';
        default:
            return 'undefined';
    }
};

export const getNewLessonParams = () => {
    const newLessonParams = {
        parentOwnerId: getId(),
        userId: getId(),
        ...getOwnerDetails()
    };
    if (isSchoolAdmin()) {
        newLessonParams.owner = {
            name: getSchoolName(),
            avatar: getSchoolAvatar(),
            id: getId()
        };
    }
    return newLessonParams;
};

export const getUserLocale = () => userStore.user?.userProperties?.userLocale;

export function getUserPlanForAnalytics() {
    if (isSchoolUser()) {
        return SubscriptionPlans.School;
    }
    switch (getUserPlan()) {
        case SubscriptionPlans.Free:
        case SubscriptionPlans.Trial:
            if (!getUserStatus()) {
                return getUserStatus();
            }
            return SubscriptionPlans.Trial;
        case SubscriptionPlans.Unlimited:
            return getUserPaymentType();
        case SubscriptionPlans.School:
            return SubscriptionPlans.School;
        case SubscriptionPlans.Basic:
        case SubscriptionPlans.Academy:
        case SubscriptionPlans.Conservatory:
        case SubscriptionPlans.Custom:
        default:
            return getUserPlan();
    }
}

export function isHidePricing() {
    return isSchoolUser() || isStudent();
}

export function isBlockAccess() {
    return isStudent() && !manageStore.hasActivePayment && !isSchoolUser();
}

export const getStudentStatusText = (status, text) => {
    return status in STUDENT_STATUS_INFO ? STUDENT_STATUS_INFO[status][text] : 'manage.student.status.deprecated';
};

